import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileWarning, Lock, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileQuantityStepper } from '@/components/ui/MobileQuantityStepper';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';

import { useAuthStore } from '@/app/store/authStore';
import {
    attachPricing,
    listCategories,
    listItemsByIds,
    listTaxRates,
    listUnits,
} from '@/app/services/catalogService';
import { getSalesOrder, updateSalesOrderItems } from '@/app/services/salesOrderService';
import { formatInr, formatQuantity } from '@/app/lib/pricing';
import type { PricedItem } from '@/app/types/item';

import { useOrderEditor, type EditorLine } from './useOrderEditor';
import { AddProductsSheet } from './components/AddProductsSheet';
import { TierSheet } from './components/TierSheet';

const REFERENCE_QUERY_OPTIONS = { staleTime: Infinity, gcTime: Infinity } as const;

/**
 * Statuses a manager may still change from the phone.
 *
 * Only `draft` qualifies: everything else means the office has already acted on the
 * order — `converted` in particular has an invoice built from these exact lines, and
 * editing behind it would leave the two disagreeing.
 */
const EDITABLE_STATUSES = new Set(['draft']);

function statusBadge(status: string) {
    switch (status) {
        case 'draft':
            return { label: 'Pending review', variant: 'warning' as const };
        case 'converted':
        case 'invoiced':
        case 'completed':
            return { label: 'Invoiced', variant: 'success' as const };
        case 'cancelled':
            return { label: 'Cancelled', variant: 'destructive' as const };
        case 'confirmed':
            return { label: 'Confirmed', variant: 'default' as const };
        default:
            return { label: status, variant: 'secondary' as const };
    }
}

function lockedReason(status: string): string {
    switch (status) {
        case 'converted':
        case 'invoiced':
        case 'completed':
            return 'This order has already been invoiced, so its lines are fixed. Call the office for any change.';
        case 'cancelled':
            return 'This order was cancelled and can no longer be changed.';
        default:
            return 'The office has already picked this order up, so its lines are fixed. Call them for any change.';
    }
}

function formatDate(iso: string): string {
    try {
        return format(new Date(`${iso}T00:00:00`), 'dd MMM yyyy');
    } catch {
        return iso;
    }
}

export default function SalesOrderDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);

    const orderId = Number(id);
    const [addOpen, setAddOpen] = useState(false);
    const [tierItem, setTierItem] = useState<PricedItem | null>(null);

    const {
        data: order,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['sales-order', orderId],
        enabled: Number.isFinite(orderId) && orderId > 0,
        queryFn: () => getSalesOrder(orderId),
    });

    const { data: units } = useQuery({
        queryKey: ['units'],
        queryFn: listUnits,
        ...REFERENCE_QUERY_OPTIONS,
    });
    const { data: taxRates } = useQuery({
        queryKey: ['tax-rates'],
        queryFn: listTaxRates,
        ...REFERENCE_QUERY_OPTIONS,
    });
    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: listCategories,
        ...REFERENCE_QUERY_OPTIONS,
    });

    const siteId = order?.site_id ?? user?.sites?.[0]?.id ?? null;
    const itemIds = useMemo(
        () => (order?.items ?? []).map((line) => line.item_id),
        [order]
    );
    // Priced as the order: the server applies the mode stamped on it at creation.
    const pricingTarget = useMemo(() => ({ documentId: orderId, siteId }), [orderId, siteId]);

    /**
     * Live pricing for the products already on the order.
     *
     * Needed so a changed quantity can be re-priced against the current ladder;
     * without it the editor falls back to the saved rate, which is still correct but
     * cannot follow a bulk tier.
     */
    const { data: linePricing, isLoading: pricingLoading } = useQuery({
        queryKey: ['so-line-pricing', orderId, itemIds, siteId],
        enabled: !!order && !!units && !!taxRates && itemIds.length > 0,
        staleTime: 60_000,
        queryFn: async (): Promise<{
            byItemId: Map<number, PricedItem>;
            priceModeLabel: string | null;
        }> => {
            const items = await listItemsByIds(itemIds);
            const priced = await attachPricing(items, units!, taxRates!, pricingTarget);
            return {
                byItemId: new Map(priced.items.map((p) => [p.id, p])),
                priceModeLabel: priced.priceModeLabel,
            };
        },
    });
    const pricedByItemId = linePricing?.byItemId;
    const priceModeLabel = linePricing?.priceModeLabel ?? null;

    // Seed the editor only once pricing has settled, so a line does not first appear
    // at its saved rate and then jump when the ladder loads a moment later.
    const pricingSettled = itemIds.length === 0 || !pricingLoading;
    const editor = useOrderEditor(
        pricingSettled ? order ?? null : null,
        pricedByItemId ?? null,
        units
    );
    const { lines, grandTotal, isDirty, setQuantity, addItem, getQuantity, toPayload } =
        editor;

    const editable = !!order && EDITABLE_STATUSES.has(order.status);

    const mutation = useMutation({
        mutationFn: () => updateSalesOrderItems(orderId, toPayload()),
        onSuccess: (updated) => {
            queryClient.setQueryData(['sales-order', orderId], updated);
            queryClient.invalidateQueries({ queryKey: ['my-sales-orders'] });
            toast.success('Order updated');
            navigate('/sales-orders');
        },
        onError: (err: unknown) => {
            let message = 'Failed to save changes';
            if (axios.isAxiosError(err)) {
                message = err.response?.data?.detail || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            toast.error(message);
        },
    });

    if (isLoading || (!!order && !pricingSettled)) {
        return (
            <div className="flex flex-col min-h-full bg-background">
                <MobileHeader title="Order" showBack backPath="/sales-orders" />
                <div className="p-4 space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="mobile-card space-y-2">
                            <MobileSkeleton className="h-4 w-44" />
                            <MobileSkeleton className="h-3 w-28" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (isError || !order) {
        return (
            <div className="flex flex-col min-h-full bg-background">
                <MobileHeader title="Order" showBack backPath="/sales-orders" />
                <MobileEmptyState
                    icon={<FileWarning className="h-12 w-12" />}
                    title="Order not found"
                    description="It may have been removed, or belongs to another branch."
                    action={
                        <MobileButton onClick={() => navigate('/sales-orders')}>
                            Back to orders
                        </MobileButton>
                    }
                />
            </div>
        );
    }

    const badge = statusBadge(order.status);
    // Saved lines are what the office currently sees; the editor holds what would
    // replace them. They diverge only while there are unsaved edits.
    const savedTotal = Number(order.total_amount ?? 0);

    return (
        <div
            className="flex flex-col bg-background"
            style={{ minHeight: '100dvh', height: '100dvh' }}
        >
            <MobileHeader
                title={order.document_number}
                showBack
                backPath="/sales-orders"
                rightAction={
                    editable ? (
                        <button
                            type="button"
                            aria-label="Add products"
                            onClick={() => setAddOpen(true)}
                            className="flex items-center justify-center w-10 h-10 rounded-full text-primary active:bg-accent"
                        >
                            <Plus className="h-6 w-6" />
                        </button>
                    ) : undefined
                }
            />

            <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-4 pt-3">
                    <div className="mobile-card">
                        <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-foreground truncate">
                                {order.party?.name ?? 'Unknown customer'}
                            </p>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {priceModeLabel && (
                                    <MobileBadge variant="secondary" className="whitespace-nowrap flex-shrink-0">{priceModeLabel}</MobileBadge>
                                )}
                                <MobileBadge variant={badge.variant}>{badge.label}</MobileBadge>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {formatDate(order.transaction_date)}
                            {order.site?.name ? ` · ${order.site.name}` : ''}
                        </p>
                        {order.expected_delivery_date && (
                            <p className="text-xs text-muted-foreground mt-1">
                                Deliver by {formatDate(order.expected_delivery_date)}
                            </p>
                        )}
                    </div>
                </div>

                {!editable && (
                    <div className="px-4 pt-3">
                        <div className="flex gap-3 rounded-xl border border-border bg-secondary/40 p-3">
                            <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-muted-foreground">
                                {lockedReason(order.status)}
                            </p>
                        </div>
                    </div>
                )}

                <h2 className="mobile-section-header pt-4">
                    {lines.length} {lines.length === 1 ? 'item' : 'items'}
                </h2>

                {lines.length === 0 ? (
                    <MobileEmptyState
                        icon={<Trash2 className="h-12 w-12" />}
                        title="No items left"
                        description="An order needs at least one line. Add a product or discard your changes."
                        action={
                            <MobileButton onClick={() => setAddOpen(true)}>
                                Add products
                            </MobileButton>
                        }
                    />
                ) : (
                    <ul className="divide-y divide-border">
                        {lines.map((line) => (
                            <OrderLineRow
                                key={line.itemId}
                                line={line}
                                editable={editable}
                                onQuantityChange={setQuantity}
                                onShowTiers={setTierItem}
                            />
                        ))}
                    </ul>
                )}

                {order.notes && (
                    <section className="px-4 pt-5 pb-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            Notes
                        </h3>
                        <p className="text-sm text-muted-foreground break-words">
                            {order.notes}
                        </p>
                    </section>
                )}

                <div className="h-6" />
            </div>

            <div
                className="border-t border-border bg-background flex-shrink-0"
                style={{
                    paddingTop: 12,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
                }}
            >
                <div className="flex items-baseline justify-between mb-3">
                    <span className="text-sm text-muted-foreground">
                        {isDirty ? 'New total' : 'Order total'}
                    </span>
                    <div className="text-right">
                        {isDirty && savedTotal > 0 && (
                            <span className="text-sm text-muted-foreground line-through mr-2 tabular-nums">
                                {formatInr(savedTotal)}
                            </span>
                        )}
                        <span className="text-2xl font-bold text-foreground tabular-nums">
                            {formatInr(isDirty ? grandTotal : savedTotal)}
                        </span>
                    </div>
                </div>

                {editable ? (
                    <MobileButton
                        type="button"
                        size="lg"
                        className="w-full"
                        disabled={!isDirty || lines.length === 0 || mutation.isPending}
                        isLoading={mutation.isPending}
                        onClick={() => mutation.mutate()}
                    >
                        {isDirty ? 'Save changes' : 'No changes'}
                    </MobileButton>
                ) : (
                    <MobileButton
                        type="button"
                        size="lg"
                        variant="secondary"
                        className="w-full"
                        onClick={() => navigate('/sales-orders')}
                    >
                        Back to orders
                    </MobileButton>
                )}
            </div>

            <AddProductsSheet
                open={addOpen}
                onClose={() => setAddOpen(false)}
                pricingTarget={pricingTarget}
                units={units}
                taxRates={taxRates}
                categories={categories}
                getQuantity={getQuantity}
                onQuantityChange={addItem}
                onShowTiers={setTierItem}
                lineCount={lines.length}
            />

            <TierSheet
                item={tierItem}
                quantity={tierItem ? getQuantity(tierItem.id) : 0}
                onClose={() => setTierItem(null)}
            />
        </div>
    );
}

interface OrderLineRowProps {
    line: EditorLine;
    editable: boolean;
    onQuantityChange: (itemId: number, quantity: number) => void;
    onShowTiers: (item: PricedItem) => void;
}

function OrderLineRow({ line, editable, onQuantityChange, onShowTiers }: OrderLineRowProps) {
    return (
        <li className="flex items-center gap-3 px-4 py-3 min-h-[72px]">
            <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground leading-snug break-words">
                    {line.name}
                </p>

                <div className="flex items-center gap-x-2 gap-y-0.5 mt-1 flex-wrap">
                    <span className="text-sm text-muted-foreground tabular-nums">
                        {formatInr(line.rate)} / {line.unitName}
                    </span>
                    {line.tierLabel && (
                        <span className="text-[11px] font-medium text-green-500 whitespace-nowrap">
                            {line.tierLabel}
                        </span>
                    )}
                    {line.repriced && line.savedRate != null && (
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap tabular-nums">
                            was {formatInr(line.savedRate)}
                        </span>
                    )}
                    {line.priced && (
                        <button
                            type="button"
                            onClick={() => onShowTiers(line.priced!)}
                            className="text-[11px] font-medium text-green-500 border border-green-500/40 rounded-full px-2 py-0.5 whitespace-nowrap active:bg-green-500/10"
                        >
                            Bulk pricing
                        </button>
                    )}
                </div>

                <p className="text-sm font-semibold text-foreground mt-1 tabular-nums">
                    {formatInr(line.totals.totalAmount)}
                    {line.changed && line.savedQuantity != null && (
                        <span className="text-[11px] font-normal text-muted-foreground ml-2">
                            was {formatQuantity(line.savedQuantity)}
                        </span>
                    )}
                    {line.savedQuantity === null && (
                        <span className="text-[11px] font-normal text-primary ml-2">added</span>
                    )}
                </p>
            </div>

            {editable ? (
                <>
                    <MobileQuantityStepper
                        value={line.quantity}
                        onValueChange={(q) => onQuantityChange(line.itemId, q)}
                        aria-label={line.name}
                    />
                    <button
                        type="button"
                        aria-label={`Remove ${line.name}`}
                        onClick={() => onQuantityChange(line.itemId, 0)}
                        className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground active:bg-accent flex-shrink-0"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </>
            ) : (
                <span className="text-base font-semibold text-foreground tabular-nums flex-shrink-0">
                    {formatQuantity(line.quantity)} {line.unitName}
                </span>
            )}
        </li>
    );
}
